import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Badge } from "@shared/components/ui/badge";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import {
  Package as PackageIcon,
  Search,
  AlertCircle,
  Loader2,
  MapPin,
  Phone,
  User,
  Truck,
  ArrowRight,
  Building,
  DollarSign,
  Barcode,
  CalendarClock,
  CalendarCheck,
  Activity,
} from "lucide-react";
import * as packagesService from "../services/packagesService";
import { STATUS_COLORS, STATUS_OPTIONS } from "@shared/utils/constants";
import { format } from "date-fns";
import { financial } from "@shared/utils/helper";
import TrackingPackages from "@features/packages/components/TrackingPackages";

// Reusable InfoItem component inspired by PackageDetails
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="grid grid-cols-[24px_1fr_2fr] gap-4 items-center py-2 border-b last:border-b-0">
    <Icon className="w-5 h-5 text-muted-foreground" />
    <div className="font-medium text-sm text-muted-foreground">{label}</div>
    <div className="text-right text-sm">{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const statusClass =
    STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";

  return (
    <Badge className={`${statusClass} hover:${statusClass}`}>
      {t(STATUS_OPTIONS[status] || `status.${status}`)}
    </Badge>
  );
};

const PackageCard = ({ packageData, onClick }) => {
  const { t } = useTranslation();

  return (
    <Card
      className="hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer overflow-hidden group"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {packageData.trackingNumber && (
                <Badge variant="outline" className="font-mono text-xs">
                  <Barcode className="w-3 h-3 mr-1" />
                  {packageData.trackingNumber}
                </Badge>
              )}
              {packageData.reference && (
                <Badge variant="secondary" className="font-mono text-xs">
                  #{packageData.reference}
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-lg text-gray-900 mb-1">
              {packageData.customer?.name}
            </h3>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" />
                {packageData.customer?.phone}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {packageData.customer?.city}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">
                {financial(packageData.totalAmount)}
                <span className="text-sm ml-1">{t("main.currency")}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {format(new Date(packageData.createdAt), "MMM d, yyyy")}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <StatusBadge status={packageData.status} />
          <ArrowRight className="w-5 h-5 text-blue-500 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
};

const TrackPackage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIdentifier, setSearchIdentifier] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trackPackage", searchIdentifier],
    queryFn: () => packagesService.trackPackageByIdentifier(searchIdentifier),
    enabled: !!searchIdentifier && searchIdentifier.trim().length > 0,
    retry: 1,
  });

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSearchIdentifier(searchQuery.trim());
      setSelectedPackage(null);
      refetch();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const isMultiplePackages = data?.multiplePackages;
  const packages = data?.packages || [];
  const packageData = isMultiplePackages
    ? selectedPackage || null
    : data?.package;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        {/* Simple Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {t("packages.trackPackage")}
          </h1>
          <p className="text-sm text-gray-600">
            {t("packages.trackPackageDescription")}
          </p>
        </div>

        {/* Search Bar */}
        <Card className="max-w-4xl">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t("packages.enterSearchQuery")}
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                onClick={handleSearch}
                disabled={isLoading || !searchQuery.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("packages.searching")}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {t("packages.search")}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="max-w-4xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error?.response?.data?.message ||
                error.message ||
                t("packages.trackingFetchFailed")}
            </AlertDescription>
          </Alert>
        )}

        {/* Multiple Packages List */}
        {isMultiplePackages && !selectedPackage && (
          <div className="max-w-4xl space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <PackageIcon className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                {t("packages.multiplePackagesFound", { count: data.count })}
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              {packages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  packageData={pkg}
                  onClick={() => setSelectedPackage(pkg)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Package Details View */}
        {packageData && (
          <div className="space-y-4">
            {isMultiplePackages && selectedPackage && (
              <Button
                variant="ghost"
                onClick={() => setSelectedPackage(null)}
                size="sm"
              >
                <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                {t("packages.backToList")}
              </Button>
            )}

            <div className="flex flex-col lg:flex-row gap-4">
              {/* Main Info Column */}
              <div className="grid gap-4 flex-1">
                {/* Package Info Card */}
                <Card>
                  <CardHeader className="bg-gray-50 border-b">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <PackageIcon className="w-5 h-5" />
                        {t("packages.packageInformation")}
                      </CardTitle>
                      <StatusBadge status={packageData.status} />
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid">
                      {packageData.trackingNumber && (
                        <InfoItem
                          icon={Barcode}
                          label={t("packages.trackingNumber")}
                          value={
                            <span className="font-mono">
                              {packageData.trackingNumber
                                ? packageData.trackingNumber
                                : "N/A"}
                            </span>
                          }
                        />
                      )}
                      {packageData.reference && (
                        <InfoItem
                          icon={PackageIcon}
                          label={t("packages.reference")}
                          value={
                            <span className="font-mono">
                              #{packageData.reference}
                            </span>
                          }
                        />
                      )}
                      <InfoItem
                        icon={Activity}
                        label={t("status.status")}
                        value={<StatusBadge status={packageData.status} />}
                      />
                      <InfoItem
                        icon={DollarSign}
                        label={t("packages.totalAmount")}
                        value={
                          <span className="font-semibold text-green-600">
                            {financial(packageData.totalAmount)}{" "}
                            {t("main.currency")}
                          </span>
                        }
                      />
                      <InfoItem
                        icon={CalendarClock}
                        label={t("common.created")}
                        value={format(
                          new Date(packageData.createdAt),
                          "dd/MM/yyyy HH:mm"
                        )}
                      />
                      {packageData.updatedAt && (
                        <InfoItem
                          icon={CalendarCheck}
                          label={t("common.updated")}
                          value={format(
                            new Date(packageData.updatedAt),
                            "dd/MM/yyyy HH:mm"
                          )}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Customer Info Card */}
                <Card>
                  <CardHeader className="bg-gray-50 border-b">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <User className="w-5 h-5" />
                      {t("packages.customerInformation")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid">
                      <InfoItem
                        icon={User}
                        label={t("packages.customerName")}
                        value={packageData.customer?.name}
                      />
                      <InfoItem
                        icon={Phone}
                        label={t("packages.customerPhone")}
                        value={packageData.customer?.phone}
                      />
                      <InfoItem
                        icon={Building}
                        label={t("packages.customerCity")}
                        value={packageData.customer?.city}
                      />
                      {packageData.customer?.secteur && (
                        <InfoItem
                          icon={MapPin}
                          label={t("packages.customerSecteur")}
                          value={packageData.customer.secteur}
                        />
                      )}
                      {packageData.customer?.address && (
                        <InfoItem
                          icon={MapPin}
                          label={t("packages.customerAddress")}
                          value={packageData.customer.address}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Delivery Person */}
                {packageData.livreur && (
                  <Card>
                    <CardHeader className="bg-gray-50 border-b">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Truck className="w-5 h-5" />
                        {t("packages.deliveryPerson")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid">
                        <InfoItem
                          icon={User}
                          label={t("packages.livreurName")}
                          value={packageData.livreur?.fullName}
                        />
                        <InfoItem
                          icon={Phone}
                          label={t("packages.livreurPhone")}
                          value={packageData.livreur?.phone}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Products */}
                {packageData.products && packageData.products.length > 0 && (
                  <Card>
                    <CardHeader className="bg-gray-50 border-b">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <PackageIcon className="w-5 h-5" />
                        {t("packages.productDetails")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        {packageData.products.map((product, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {product.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {product.quantity} × {financial(product.price)}{" "}
                                {t("main.currency")}
                              </p>
                            </div>
                            <p className="font-semibold text-green-600">
                              {financial(product.quantity * product.price)}{" "}
                              {t("main.currency")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Package Note */}
                {packageData.note && (
                  <Card>
                    <CardHeader className="bg-gray-50 border-b">
                      <CardTitle className="text-base font-semibold">
                        {t("packages.note")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="bg-muted p-3 rounded-md text-sm">
                        {packageData.note}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Timeline Column - Using TrackingPackages component */}
              <div className="min-w-[400px]">
                <TrackingPackages statusHistory={packageData?.statusHistory} />
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!packageData &&
          !isMultiplePackages &&
          !isLoading &&
          !error &&
          searchIdentifier === "" && (
            <Card className="max-w-4xl py-12">
              <CardContent className="text-center space-y-3">
                <PackageIcon className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("packages.noPackageSearched")}
                </h3>
                <p className="text-sm text-gray-600">
                  {t("packages.enterSearchPrompt")}
                </p>
              </CardContent>
            </Card>
          )}
      </div>
    </div>
  );
};

export default TrackPackage;
